import { createReducer } from '@reduxjs/toolkit';

import {
  CONTENT_WINDOW_DEFAULT_HEIGHT,
  CONTENT_WINDOW_DEFAULT_WIDTH,
  CONTENT_WINDOW_GAP,
  WINDOW_AMOUNT_LIMIT,
} from '../../constants/contentWindow';
import { IAction } from '../../types/redux';
import { ILayoutRestoreAction, LAYOUT_RESTORE } from '../layout';

import {
  CONTENT_WINDOWS_APPEND,
  CONTENT_WINDOWS_SET_IS_OPEN,
  CONTENT_WINDOWS_REMOVE,
  CONTENT_WINDOWS_RESET,
  IContentWindowsAppendAction,
  IContentWindowsSetIsOpenAction,
  IContentWindowsRemoveAction,
  IContentWindowsState,
  CONTENT_WINDOWS_UPDATE_GEOMETRY,
  IContentWindowsUpdateGeometryAction,
} from './contentWindows.types';

const INITIAL_STATE: IContentWindowsState = {
  list: [],
  settings: {},
};

export const contentWindowsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(CONTENT_WINDOWS_RESET, (state: IContentWindowsState) => {
      state.list = [];
      // Без этого settings копил бы записи закрытых окон, а list и settings
      // разъезжались бы — WindowTabsPanel читает settings[nodeId] без guard'а.
      state.settings = {};
    })
    .addCase(
      CONTENT_WINDOWS_APPEND,
      (state: IContentWindowsState, action: IAction<IContentWindowsAppendAction>) => {
        if (!action.payload) {
          return;
        }

        const idx = state.list.indexOf(action.payload.nodeIdToAppend);

        if (idx === -1) {
          if (state.list.length >= WINDOW_AMOUNT_LIMIT) {
            const evicted = state.list.slice(0, state.list.length - WINDOW_AMOUNT_LIMIT + 1);

            // Раньше вытесненный id выбрасывался только из list, а его настройки
            // оставались в settings навсегда: список ключей рос без предела и
            // разъезжался с list.
            evicted.forEach((evictedId) => {
              delete state.settings[evictedId];
            });

            state.list = state.list.slice(evicted.length);
          }

          const amount = state.list.length + 1;

          state.list = [...state.list, action.payload.nodeIdToAppend];
          state.settings[action.payload.nodeIdToAppend] = {
            isOpen: true,
            coords: {
              x: CONTENT_WINDOW_GAP * amount,
              y: CONTENT_WINDOW_GAP * amount,
            },
            size: {
              width: CONTENT_WINDOW_DEFAULT_WIDTH,
              height: CONTENT_WINDOW_DEFAULT_HEIGHT,
            },
            scrollPercent: 0,
          };
          return;
        }

        state.list.splice(idx, 1);

        state.list = [...state.list, action.payload.nodeIdToAppend];
        state.settings[action.payload.nodeIdToAppend].isOpen = true;
      },
    )
    .addCase(
      CONTENT_WINDOWS_REMOVE,
      (state: IContentWindowsState, action: IAction<IContentWindowsRemoveAction>) => {
        if (!action.payload) {
          return;
        }

        const updated = [...state.list];

        const idx = updated.indexOf(action.payload.nodeIdToRemove);

        if (idx === -1) {
          return;
        }

        updated.splice(idx, 1);

        state.list = updated;
        delete state.settings[action.payload.nodeIdToRemove];
      },
    )
    .addCase(
      CONTENT_WINDOWS_SET_IS_OPEN,
      (state: IContentWindowsState, action: IAction<IContentWindowsSetIsOpenAction>) => {
        if (
          !action.payload ||
          typeof action.payload.nodeId === 'undefined' ||
          typeof action.payload.isOpen === 'undefined'
        ) {
          return;
        }

        const { nodeId, isOpen } = action.payload;

        state.settings[nodeId].isOpen = isOpen;
      },
    )
    .addCase(
      CONTENT_WINDOWS_UPDATE_GEOMETRY,
      (state: IContentWindowsState, action: IAction<IContentWindowsUpdateGeometryAction>) => {
        if (!action.payload || typeof action.payload.nodeId === 'undefined') {
          return;
        }

        const { nodeId, coords, size, scrollPercent } = action.payload;
        const settings = state.settings[nodeId];

        // Окно уже закрыли (или id чужой) — молча выходим, не создавая запись:
        // осиротевший settings без элемента в list роняет WindowTabsPanel.
        if (!settings) {
          return;
        }

        if (coords) {
          settings.coords = coords;
        }

        if (size) {
          settings.size = size;
        }

        if (typeof scrollPercent === 'number') {
          settings.scrollPercent = scrollPercent;
        }
      },
    )
    .addCase(
      LAYOUT_RESTORE,
      (state: IContentWindowsState, action: IAction<ILayoutRestoreAction>) => {
        if (!action.payload?.layout) {
          return;
        }

        // Именно ЗАМЕНА, не merge. Restore прилетает на каждый PROJECT_SET, поэтому
        // полная замена по построению не даёт окнам предыдущего проекта протечь
        // в следующий — раньше они просто оставались висеть.
        const { list, settings } = action.payload.layout.contentWindows;

        state.list = [...list];
        state.settings = { ...settings };
      },
    );
});
